import * as THREE from 'three';

type ParticleOptions = {
  speed?: number;
  lift?: number;
  gravity?: number;
  life?: number;
  spread?: number;
};

export class SplashParticles {
  readonly points: THREE.Points;
  private readonly positions: Float32Array;
  private readonly colors: Float32Array;
  private readonly velocity: THREE.Vector3[];
  private readonly gravity: Float32Array;
  private readonly life: Float32Array;
  private readonly maxLife: Float32Array;
  private cursor = 0;

  constructor(private readonly capacity = 320) {
    this.positions = new Float32Array(capacity * 3);
    this.colors = new Float32Array(capacity * 3);
    this.velocity = Array.from({ length: capacity }, () => new THREE.Vector3());
    this.gravity = new Float32Array(capacity);
    this.life = new Float32Array(capacity);
    this.maxLife = new Float32Array(capacity);
    this.reset();

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(this.positions, 3));
    geometry.setAttribute('color', new THREE.BufferAttribute(this.colors, 3));
    const material = new THREE.PointsMaterial({
      size: 0.22,
      sizeAttenuation: true,
      vertexColors: true,
      transparent: true,
      opacity: 0.92,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });
    this.points = new THREE.Points(geometry, material);
    this.points.frustumCulled = false;
    this.points.renderOrder = 4;
  }

  emit(position: THREE.Vector3, color: number, count: number, options: ParticleOptions = {}): void {
    const tint = new THREE.Color(color);
    const speed = options.speed ?? 2.8;
    const lift = options.lift ?? 2.2;
    const spread = options.spread ?? 0.32;
    const particleLife = options.life ?? 0.7;
    for (let emitted = 0; emitted < count; emitted += 1) {
      const index = this.cursor;
      this.cursor = (this.cursor + 1) % this.capacity;
      const offset = index * 3;
      this.positions[offset] = position.x + (Math.random() - 0.5) * spread;
      this.positions[offset + 1] = position.y + (Math.random() - 0.5) * spread;
      this.positions[offset + 2] = position.z + (Math.random() - 0.5) * spread;
      this.colors[offset] = tint.r;
      this.colors[offset + 1] = tint.g;
      this.colors[offset + 2] = tint.b;
      const angle = Math.random() * Math.PI * 2;
      const radial = speed * (0.35 + Math.random() * 0.65);
      this.velocity[index].set(
        Math.cos(angle) * radial,
        lift * (0.55 + Math.random() * 0.8),
        Math.sin(angle) * radial,
      );
      this.gravity[index] = options.gravity ?? 5.5;
      this.life[index] = particleLife * (0.65 + Math.random() * 0.55);
      this.maxLife[index] = this.life[index];
    }
    this.markDirty();
  }

  update(dt: number): void {
    let changed = false;
    for (let index = 0; index < this.capacity; index += 1) {
      if (this.life[index] <= 0) continue;
      this.life[index] = Math.max(0, this.life[index] - dt);
      const offset = index * 3;
      if (this.life[index] <= 0) {
        this.positions[offset] = 9999;
        this.positions[offset + 1] = 9999;
        this.positions[offset + 2] = 9999;
        changed = true;
        continue;
      }
      this.velocity[index].y -= this.gravity[index] * dt;
      this.positions[offset] += this.velocity[index].x * dt;
      this.positions[offset + 1] += this.velocity[index].y * dt;
      this.positions[offset + 2] += this.velocity[index].z * dt;
      const brightness = 0.45 + 0.55 * (this.life[index] / this.maxLife[index]);
      this.colors[offset] *= brightness > 0.7 ? 1 : 0.975;
      this.colors[offset + 1] *= brightness > 0.7 ? 1 : 0.975;
      this.colors[offset + 2] *= brightness > 0.7 ? 1 : 0.975;
      changed = true;
    }
    if (changed) this.markDirty();
  }

  reset(): void {
    this.life?.fill(0);
    for (let index = 0; index < this.capacity; index += 1) {
      const offset = index * 3;
      this.positions[offset] = 9999;
      this.positions[offset + 1] = 9999;
      this.positions[offset + 2] = 9999;
    }
    if (this.points) this.markDirty();
  }

  private markDirty(): void {
    const geometry = this.points.geometry;
    geometry.getAttribute('position').needsUpdate = true;
    geometry.getAttribute('color').needsUpdate = true;
  }
}
